import React, { Component } from 'react';
import { arrayOf, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { injectIntl, intlShape } from 'react-intl';
import { path, pathOr } from 'ramda';

import { FieldNumericStepper, FilterPlain, FilterPopup } from '../../components';

import css from './CapacityFilter.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'a,' }
const format = (values, queryParamName) => {
  const capacity = pathOr(0, ['capacity'], values);
  const children = pathOr(0, ['children'], values);
  const infants = pathOr(0, ['infants'], values);
  const totalParticipants = capacity + children;

  const maxValue = totalParticipants > 0 ? `${totalParticipants},` : null;
  const minValue = totalParticipants> 0 ? `,${totalParticipants + 1}` : null;
  const queryParams = {
    [queryParamName]: maxValue,
    'pub_minCapacityToGuest': minValue,
    'pub_enfantsBienvenue': children > 0 ? true : null,
    children: children > 0 ? children : null,
    infants: infants > 0 ? infants : null,
  };

  return queryParams;
};

const formatInitialValues = (capacity) => {
  const value = parseInt(capacity.slice(0, -1), 10);
  return value;
};

class CapacityFilter extends Component {
  constructor(props) {
    super(props);

    this.filter = null;
    this.filterContent = null;

    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      options,
      initialValues,
      contentPlacementOffset,
      onSubmit,
      queryParamNames,
      searchMode,
      intl,
      showAsPopup,
      helper,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const filterWrapperClasses = classNames(css.filtersWrapper, { [css.filtersWrapperPopup]: showAsPopup });

    const queryParamName = getQueryParamName(queryParamNames);
    const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
    const childrenInitialValues = hasInitialValues && path(['children'], initialValues);
    const infantsInitialValues = hasInitialValues && path(['infants'], initialValues);

    const contentStyle = this.positionStyleForContent();

    // pass the initial values with the name key so that
    // they can be passed to the correct field
    const namedInitialValues = {
      [name]: hasInitialValues ? formatInitialValues(initialValues[queryParamName]) - childrenInitialValues : 0,
      children: childrenInitialValues ? parseInt(initialValues['children']) : 0,
      infants: infantsInitialValues ? parseInt(initialValues['infants']) : 0,
    };

    const handleSubmit = values => {
      onSubmit(format(values, queryParamName));
    };

    const labelText = intl.formatMessage({ id: label });
    const helperText = intl.formatMessage({ id: helper });
    const adultsLabel = intl.formatMessage({ id: 'CapacityFilter.adultsLabel' });
    const childrenLabel = intl.formatMessage({ id: 'CapacityFilter.childrenLabel' });
    const childrenHelper = intl.formatMessage({ id: 'CapacityFilter.childrenHelper' });
    const infantsLabel = intl.formatMessage({ id: 'CapacityFilter.infantsLabel' });
    const infantsHelper = intl.formatMessage({ id: 'CapacityFilter.infantsHelper' });

    return showAsPopup ? (
      <FilterPopup
        className={classes}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        name={name}
        label={labelText}
        isSelected={hasInitialValues}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        keepDirtyOnReinitialize
        {...rest}
      >
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name={name}
          id={id}
          label={adultsLabel}
          helper={helperText}
        />
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name="children"
          id="children"
          label={childrenLabel}
          helper={childrenHelper}
        />
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name="infants"
          id="infants"
          label={infantsLabel}
          helper={infantsHelper}
        />
      </FilterPopup>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={labelText}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentStyle}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        {...rest}
      >
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name={name}
          id={id}
          label={adultsLabel}
          helper={helperText}
        />
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name="children"
          id="children"
          label={childrenLabel}
          helper={childrenHelper}
        />
        <FieldNumericStepper
          className={filterWrapperClasses}
          inputClassName={css.input}
          stepperClassName={css.stepper}
          min={0}
          name="infants"
          id="infants"
          label={infantsLabel}
          helper={infantsHelper}
        />
      </FilterPlain>
    );
  }
}

CapacityFilter.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  contentPlacementOffset: 0,
};

CapacityFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  contentPlacementOffset: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(CapacityFilter);



// WEBPACK FOOTER //
// ./src/components/CapacityFilter/CapacityFilter.js